import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Chip,
  Button,
  useMediaQuery,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

const AutomationFilter = ({
  sortBy,
  setSortBy,
  setFilterText,
  onTagsChange,
  onEnabledChange,
  onErrorChange,
  tags,
  type,
}) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(window.location.search);
  const [searchInput, setSearchInput] = useState(urlParams.get("filter") || "");
  const [searchValue, setSearchValue] = useState("");
  const [selectedTagsLocal, setSelectedTagsLocal] = useState(() => {
    const tags = urlParams.get("tags");
    return tags ? tags.split(",") : ["all"];
  });
  const [selectedEnabledLocal, setSelectedEnabledLocal] = useState(() => {
    const able = urlParams.get("able");
    return able ? able.split(",") : ["all"];
  });
  const [selectedErrorLocal, setSelectedErrorLocal] = useState(() => {
    const error = urlParams.get("err");
    return error ? error.split(",") : ["all"];
  });
  const [showAllTags, setShowAllTags] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const history = useHistory();

  const VISIBLE_TAGS = isSmallScreen ? 4 : 8;
  const visibleTags = showAllTags ? tags : tags.slice(0, VISIBLE_TAGS);

  const enabledOptions = ["all", "enabled", "disabled"];
  const errorOptions = ["all", "error", "no-error"];

  useEffect(() => {
    setFilterText(searchInput);
  }, [searchInput]);

  useEffect(() => {
    if (onTagsChange) {
      onTagsChange(selectedTagsLocal);
    }
  }, [selectedTagsLocal, onTagsChange]);

  useEffect(() => {
    if (onEnabledChange) {
      onEnabledChange(selectedEnabledLocal);
    }
  }, [selectedEnabledLocal, onEnabledChange]);

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange(selectedErrorLocal);
    }
  }, [selectedErrorLocal, onErrorChange]);

  useEffect(() => {
    const updates = getUpdates();
    updateURL(updates);
  }, [
    sortBy,
    selectedTagsLocal,
    selectedEnabledLocal,
    selectedErrorLocal,
    searchInput,
  ]);

  const getUpdates = () => {
    const updates = {
      filter: searchInput,
      tags: selectedTagsLocal.join(","),
      able: selectedEnabledLocal.join(","),
      err: selectedErrorLocal.join(","),
      sort: sortBy,
    };

    if (Object.values(updates).some((value) => value && value !== "")) {
      if (type === "taskflow") {
        localStorage.setItem("filterParams-taskflow", JSON.stringify(updates));
      } else {
        localStorage.setItem("filterParams-recurring", JSON.stringify(updates));
      }
    }
    return updates;
  };

  const updateURL = (updates) => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (
        value &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newURL = `${location.pathname}?${currentParams.toString()}`;
    window.history.replaceState(null, "", newURL);
  };

  const handleParams = () => {
    const savedFilterParams =
      type === "taskflow"
        ? localStorage.getItem("filterParams-taskflow")
        : localStorage.getItem("filterParams-recurring");
    if (savedFilterParams) {
      const parsedParams = JSON.parse(savedFilterParams);
      return parsedParams;
    } else {
      return null;
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleClear = () => {
    setSearchInput("");
    setFilterText("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setSearchValue(searchInput);
    }
  };

  const handleTagChange = (tag) => {
    let newTags;
    if (tag === "all") {
      newTags = ["all"];
    } else {
      const currentTags = selectedTagsLocal.filter((t) => t !== "all");
      if (currentTags.includes(tag)) {
        newTags = currentTags.filter((t) => t !== tag);
        if (newTags.length === 0) newTags = ["all"];
      } else {
        newTags = [...currentTags, tag];
      }
    }
    setSelectedTagsLocal(newTags);
  };

  const handleEnabledChange = (option) => {
    setSelectedEnabledLocal([option]);
  };

  const handleErrorChange = (option) => {
    setSelectedErrorLocal([option]);
  };

  const toggleShowAllTags = () => {
    setShowAllTags(!showAllTags);
  };

  const renderFilterOptions = (
    options,
    selectedOptions,
    handleChange,
    label
  ) => {
    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          {!isSmallScreen && (
            <Typography
              variant="caption"
              sx={{
                mr: 2,
                minWidth: "60px",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {label}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {options.map((option) => (
              <Chip
                key={option}
                label={option.charAt(0).toUpperCase() + option.slice(1)}
                onClick={() => handleChange(option)}
                sx={{
                  backgroundColor: selectedOptions.includes(option)
                    ? "primary.main"
                    : "background.input",
                  color: selectedOptions.includes(option)
                    ? "primary.contrastText"
                    : "text.primary",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: selectedOptions.includes(option)
                      ? "primary.dark"
                      : "action.hover",
                  },
                  transition: "background-color 0.3s",
                  height: "24px",
                  borderRadius: "12px",
                  fontSize: "0.75rem",
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        width: "100%",
        borderRadius: 2,
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "8px 12px",
          width: "100%",
          gap: 1.5,
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              Name
            </Typography>
            <TextField
              fullWidth
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Filter by name"
              variant="outlined"
              size="small"
              InputProps={{
                sx: {
                  backgroundColor: "background.input",
                  "& .MuiOutlinedInput-input": {
                    padding: "6px 10px",
                  },
                },
                endAdornment: searchInput && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={{ marginRight: -1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              Sort By
            </Typography>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                }}
                sx={{
                  backgroundColor: "background.input",
                  "& .MuiOutlinedInput-input": {
                    padding: "6px 10px",
                  },
                }}
              >
                <MenuItem value="alphabet">Name (A-Z)</MenuItem>
                <MenuItem value="opp-alphabet">Name (Z-A)</MenuItem>
                <MenuItem value="time">Execution (Latest)</MenuItem>
                <MenuItem value="opp-time">Execution (Oldest)</MenuItem>
                <MenuItem value="count">Count</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {isSmallScreen ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <>
              {renderFilterOptions(
                errorOptions,
                selectedErrorLocal,
                handleErrorChange,
                "Error"
              )}
            </>
            {type !== "taskflow" &&
              renderFilterOptions(
                enabledOptions,
                selectedEnabledLocal,
                handleEnabledChange,
                "Enabled"
              )}

            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                {!isSmallScreen && (
                  <Typography
                    variant="caption"
                    sx={{
                      mr: 2,
                      minWidth: "60px",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    Tag
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {visibleTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                      onClick={() => handleTagChange(tag)}
                      sx={{
                        backgroundColor: selectedTagsLocal.includes(tag)
                          ? "primary.main"
                          : "background.input",
                        color: selectedTagsLocal.includes(tag)
                          ? "primary.contrastText"
                          : "text.primary",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: selectedTagsLocal.includes(tag)
                            ? "primary.dark"
                            : "action.hover",
                        },
                        transition: "background-color 0.3s",
                        height: "24px",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                      }}
                    />
                  ))}
                  {tags.length > VISIBLE_TAGS && (
                    <Button
                      onClick={toggleShowAllTags}
                      startIcon={
                        showAllTags ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )
                      }
                      sx={{
                        minWidth: "auto",
                        padding: "4px 8px",
                        height: "24px",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      {showAllTags ? "Less" : "More"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <>
              {renderFilterOptions(
                errorOptions,
                selectedErrorLocal,
                handleErrorChange,
                "Error"
              )}
            </>
            {type !== "taskflow" &&
              renderFilterOptions(
                enabledOptions,
                selectedEnabledLocal,
                handleEnabledChange,
                "Enabled"
              )}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                {!isSmallScreen && (
                  <Typography
                    variant="caption"
                    sx={{
                      mr: 2,
                      minWidth: "60px",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    Tag
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {visibleTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                      onClick={() => handleTagChange(tag)}
                      sx={{
                        backgroundColor: selectedTagsLocal.includes(tag)
                          ? "primary.main"
                          : "background.input",
                        color: selectedTagsLocal.includes(tag)
                          ? "primary.contrastText"
                          : "text.primary",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: selectedTagsLocal.includes(tag)
                            ? "primary.dark"
                            : "action.hover",
                        },
                        transition: "background-color 0.3s",
                        height: "24px",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                      }}
                    />
                  ))}
                  {tags.length > VISIBLE_TAGS && (
                    <Button
                      onClick={toggleShowAllTags}
                      startIcon={
                        showAllTags ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )
                      }
                      sx={{
                        minWidth: "auto",
                        padding: "4px 8px",
                        height: "24px",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      {showAllTags ? "Less" : "More"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default AutomationFilter;
